import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input } from '@jaylabs/ui';
import { joinWaitlist } from '../services/api';
import { showErrorToast, showSuccessToast } from '../hooks/useToast';
import { useAuth } from '../context/AuthContext';
import './ComingSoon.css';

function ComingSoon() {
  const { user } = useAuth();
  const [primaryForm, setPrimaryForm] = useState({ name: '', email: '' });
  const [inviteForm, setInviteForm] = useState({ name: '', email: '' });
  const [submitting, setSubmitting] = useState(null);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isAdmin = user && user.email === 'jay@jaylarson.com';

  const handleSubmit = async (payload, source, formKey) => {
    if (!payload.name.trim()) {
      showErrorToast('Name required', 'Please share your name so we can reserve your spot.');
      return;
    }
    if (!payload.email.trim() || !emailRegex.test(payload.email.trim())) {
      showErrorToast('Valid email required', 'Add a real email so we can send your invite.');
      return;
    }

    try {
      setSubmitting(formKey);
      await joinWaitlist({
        name: payload.name.trim(),
        email: payload.email.trim(),
        source,
      });
      showSuccessToast('You are on the list', 'We will send beta invites in small waves.');

      if (formKey === 'primary') {
        setPrimaryForm({ name: '', email: '' });
      } else {
        setInviteForm({ name: '', email: '' });
      }
    } catch (error) {
      console.error('Waitlist signup error:', error);

      let title = 'Request failed';
      let message = 'Please check your connection and try again.';

      if (error.response) {
        const serverMessage = error.response.data?.message || 'Something went wrong on our end.';
        const statusCode = error.response.status;

        if (statusCode === 409) {
          title = 'Already registered';
          message = serverMessage;
        } else if (serverMessage.toLowerCase().includes('too many')) {
          title = 'Slow down there';
          message = serverMessage;
        } else {
          title = 'Could not save';
          message = serverMessage;
        }
      } else if (error.request) {
        title = 'Connection issue';
        message = 'Unable to reach the server. Check your internet connection and try again.';
      } else {
        title = 'Error';
        message = error.message || 'An unexpected error occurred.';
      }

      showErrorToast(title, message);
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div
      className="dd-coming-soon-page"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0.7)), url(/backgrounds/Background-2-1024x682.jpg)`
      }}
    >
      <div className="dd-coming-soon-container">
        {/* Hero */}
        <div className="dd-coming-soon-hero">
          <img src="/DopeDash_logo.png" alt="Dope Dash" />
          <span className="dd-coming-soon-badge">Invitation-only beta</span>
          <h1 className="dd-coming-soon-title">Dope Dash is almost ready</h1>
          <p className="dd-coming-soon-desc">
            We are wiring up the last pieces. Join the waitlist to get an invite when we open
            the doors in small batches.
          </p>
          <div className="dd-vstack dd-gap-2" style={{ alignItems: 'center' }}>
            <a href="https://dopedash.app" className="dd-text-link" style={{ color: '#7c3aed', fontWeight: 500 }}>
              Visit the marketing site
            </a>
            <Link to="/login" className="dd-text-link" style={{ color: '#7c3aed', fontWeight: 500 }}>
              Already have an account? Log in
            </Link>
          </div>
        </div>

        {/* Forms */}
        <div className="dd-coming-soon-forms">
          {/* Primary waitlist */}
          <div className="dd-coming-soon-card">
            <h3>Get on the beta list</h3>
            <p>
              Add your info to reserve a spot. We will invite people gradually so nobody
              gets overwhelmed on day one.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(primaryForm, 'coming_soon_primary', 'primary');
              }}
            >
              <div className="dd-form-field">
                <label className="dd-label">Full name</label>
                <Input
                  value={primaryForm.name}
                  onChange={(e) => setPrimaryForm({ ...primaryForm, name: e.target.value })}
                  placeholder="Taylor Example"
                />
              </div>
              <div className="dd-form-field">
                <label className="dd-label">Email</label>
                <Input
                  type="email"
                  value={primaryForm.email}
                  onChange={(e) => setPrimaryForm({ ...primaryForm, email: e.target.value })}
                  placeholder="you@example.com"
                />
                {primaryForm.email !== '' && !emailRegex.test(primaryForm.email) && (
                  <span className="dd-field-error">Enter a valid email so we can reach you.</span>
                )}
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={submitting === 'primary'}
              >
                {submitting === 'primary' ? 'Joining...' : 'Join the waitlist'}
              </Button>
            </form>
          </div>

          {/* Invite wave */}
          <div className="dd-coming-soon-card">
            <h3>Request an invite wave</h3>
            <p>
              Want to be in an early invite batch? Tell us where to send it and we will slot
              you into the next wave.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(inviteForm, 'coming_soon_invite_wave', 'invite');
              }}
            >
              <div className="dd-form-field">
                <label className="dd-label">Name (or team)</label>
                <Input
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  placeholder="Jordan from Neuro Squad"
                />
              </div>
              <div className="dd-form-field">
                <label className="dd-label">Email</label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="team@example.com"
                />
                {inviteForm.email !== '' && !emailRegex.test(inviteForm.email) && (
                  <span className="dd-field-error">Enter a valid email so we can reach you.</span>
                )}
              </div>
              <Button
                type="submit"
                variant="success"
                size="lg"
                disabled={submitting === 'invite'}
              >
                {submitting === 'invite' ? 'Requesting...' : 'Request an invite'}
              </Button>
            </form>
          </div>
        </div>

        {/* Footer note */}
        <p className="dd-coming-soon-footer">
          We keep the waitlist small and send invites in waves. Thanks for your patience while
          we finish wiring things up.
        </p>
        <p className="dd-coming-soon-footer" style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: isAdmin ? '#7c3aed' : '#9ca3af', fontWeight: isAdmin ? 600 : 400 }}>
          Build {process.env.REACT_APP_BUILD_NUMBER || 'dev'} &bull; v{process.env.REACT_APP_VERSION || '0.1.0'}
        </p>
      </div>
    </div>
  );
}

export default ComingSoon;
